import styled, { css } from 'styled-components';

import { BORDER_WIDTH, BORDER_WIDTH_IMAGE, BORDER_WIDTH_OUTLINED } from './constants';
import { ContentCardPadding, ContentCardVariant } from './types';
import { getPaddingObject } from './utils';

interface CardContainerProps {
  $variant: ContentCardVariant;
  $maxWidth?: number;
  $fillHeight: boolean;
}

const CardContainer = styled.div<CardContainerProps>`
  width: 100%;
  max-width: ${({ $maxWidth }) => ($maxWidth ? `${$maxWidth}px` : 'none')};
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  position: relative;
  border-radius: ${({ theme }) => theme.shape.borderRadius * 2}px;
  height: ${({ $fillHeight }) => ($fillHeight ? '100%' : 'auto')};
  min-height: 0;
  overflow: ${({ $fillHeight }) => ($fillHeight ? 'hidden' : 'visible')};
  ${({ $variant, theme }) => {
    switch ($variant) {
      case ContentCardVariant.Contained:
        // Filled card with no border — the neutral surface tone (background.default)
        // alone delineates it against the content panel (background.canvas) in BOTH
        // light and dark, unlike the transparent Outlined variant.
        return css`
          background-color: ${theme.palette.background.default};
        `;
      case ContentCardVariant.Outlined:
        return css`
          background-color: transparent;
          border: ${BORDER_WIDTH_OUTLINED}px solid ${theme.palette.divider};
        `;
      case ContentCardVariant.Paper:
        return css`
          background-color: ${theme.palette.background.paper};
          border: ${BORDER_WIDTH}px solid ${theme.palette.divider};
        `;
      case ContentCardVariant.Dashed:
        return css`
          background-color: transparent;
          border: ${BORDER_WIDTH_IMAGE}px solid transparent;
          // border image is used as the native dashed border is too thin
          border-image: url(${() => `/dashed-border-image-${theme.palette.mode}.png`}) 33% round;
        `;
      default:
        return css``;
    }
  }}
`;

export interface ContentProps {
  $padding: ContentCardPadding;
  $variant: ContentCardVariant;
  $headered: boolean;
  $footered: boolean;
  $fillHeight: boolean;
}

const Content = styled.div<ContentProps>`
  overflow: ${({ $fillHeight }) => ($fillHeight ? 'auto' : 'visible')};
  height: ${({ $fillHeight }) => ($fillHeight ? '100%' : 'auto')};
  padding-top: ${({ theme, $headered, $padding, $variant }) =>
    $headered ? 0 : getPaddingObject(theme, $variant, $padding).top};
  padding-bottom: ${({ theme, $footered, $padding, $variant }) =>
    $footered ? 0 : getPaddingObject(theme, $variant, $padding).bottom};
  padding-left: ${({ theme, $padding, $variant }) =>
    getPaddingObject(theme, $variant, $padding).left};
  padding-right: ${({ theme, $padding, $variant }) =>
    getPaddingObject(theme, $variant, $padding).right};
`;

interface ScrollableContainerProps {
  $fillHeight: boolean;
}

// ScrollableContainer is necessary to ensure that the overflow scroll appears on the
// far end of the container, rather than within the padded content area.
const ScrollableContainer = styled.div<ScrollableContainerProps>`
  overflow: ${({ $fillHeight }) => ($fillHeight ? 'auto' : 'visible')};
  height: ${({ $fillHeight }) => ($fillHeight ? '100%' : 'auto')};
`;

export { CardContainer, Content, ScrollableContainer };
